import { Controller } from "@hotwired/stimulus"

declare global {
  interface Window {
    [RecaptchaController.onLoad]: () => void
    grecaptcha: {
      execute: (widgetId: number) => void
      getResponse: (widgetId: number) => string
      render: (el: HTMLElement, options: RenderOptions) => number
      reset: (widgetId: number) => void
    }
  }
}

type RenderOptions = {
  sitekey: string
  callback: (token: string) => void
  "expired-callback": () => void
  size: string
}

const RECAPTCHA_SCRIPT_ID = "js-grecaptcha"

export default class RecaptchaController extends Controller<HTMLInputElement> {
  static targets = ["tokenInput", "container"]
  declare readonly tokenInputTarget: HTMLInputElement
  declare readonly containerTarget: HTMLDivElement

  static readonly onLoad = "recaptchaOnLoad"

  static values = { sitekey: String }
  declare readonly sitekeyValue: string

  private widgetId = -1

  connect(): void {
    const recaptchaOnLoad = window[RecaptchaController.onLoad]

    window[RecaptchaController.onLoad] = (): void => {
      if (recaptchaOnLoad) recaptchaOnLoad()

      this.widgetId = window.grecaptcha.render(this.containerTarget, {
        sitekey: this.sitekeyValue,
        callback: (token: string) => {
          this.tokenInputTarget.value = token
          this.dispatch("tokenReceived", { detail: { token } })
        },
        "expired-callback": (): void => {
          this.tokenInputTarget.value = ""
        },
        size: "invisible"
      })
    }

    this.appendApi()
  }

  execute(): void {
    const token = window.grecaptcha.getResponse(this.widgetId)

    if (token) {
      this.tokenInputTarget.value = token
      this.dispatch("tokenReceived", { detail: { token } })
    } else {
      window.grecaptcha.reset(this.widgetId)
      window.grecaptcha.execute(this.widgetId)
    }
  }

  get hasToken(): boolean {
    return !!this.tokenInputTarget.value
  }

  private appendApi(): void {
    const api = document.getElementById(RECAPTCHA_SCRIPT_ID)

    if (!api) {
      const script = document.createElement("script")
      script.type = "text/javascript"
      script.src = `https://www.google.com/recaptcha/api.js?onload=${RecaptchaController.onLoad}&render=explicit`
      script.defer = true
      script.id = RECAPTCHA_SCRIPT_ID
      document.head.appendChild(script)
    }
  }
}
